import Image from "../../images/Image";
import { useCallback, useState } from "react";
import DropdownFramer from "../../framerMotion/DropdownFramer";
import DropdownItemsFramer from "../../framerMotion/DropdownItemsFramer";
import CheckboxMap from "../../checkbox/CheckboxMap";
import { transformString } from "../../commonfunctions/transformString";
import { getAccessPrivilegeObject } from "../../commonfunctions/getAccessPrivilageObject";
import useTranslation from "../../CustomHooks/useTranslation";
import { AccessPriviledgeContent } from "./AccessPriviledgeContent";

interface MapPrivilegeProps {
  accessItem: string;
  handelCheckBox: (id: string) => void;
  checkedArray: string[];
  lastIndex: boolean;
  editLoading?: boolean;
}

const MapPrivilege = ({ accessItem, handelCheckBox, checkedArray, lastIndex, editLoading }: MapPrivilegeProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const { translate, notEnglish } = useTranslation(AccessPriviledgeContent);
  const access = getAccessPrivilegeObject();

  const divideArray = <T,>(array: T[]): T[][] => {
    const result: T[][] = [[], [], []];
    for (let i = 0; i < array.length; i++) {
      if (i % 3 === 0) result[0].push(array[i]);
      if (i % 3 === 1) result[1].push(array[i]);
      if (i % 3 === 2) result[2].push(array[i]);
    }
    return result;
  };

  // Helper function to get the full privilege ID for the current module
  const getPrivilegeId = (action: string) => {
    return `${accessItem}_${action}`;
  };

  // Modified to handle dependencies using full privilege IDs
  const handleScopedCheckbox = useCallback((id: string) => {
    // First, handle the clicked checkbox
    handelCheckBox(id);

    // Get the base action from the ID (e.g., "user_management_view" -> "view")
    const action = id.split('_').pop() || '';

    // Define dependencies using base actions
    const dependencies: { [key: string]: string[] } = {
      'view': ['list'],
      // Add other dependencies as needed
    };

    // If this action has dependencies and was unchecked
    if (dependencies[action] && checkedArray.includes(id)) {
      // Get all dependent actions for this module
      dependencies[action].forEach(depAction => {
        const depId = getPrivilegeId(depAction);
        if (checkedArray.includes(depId)) {
          handelCheckBox(depId);
        }
      });
    }

    // If this action is a dependency of another action
    Object.entries(dependencies).forEach(([mainAction, depActions]) => {
      if (depActions.includes(action)) {
        const mainId = getPrivilegeId(mainAction);
        if (checkedArray.includes(mainId) && !checkedArray.includes(id)) {
          handelCheckBox(id);
        }
      }
    });
  }, [accessItem, checkedArray, handelCheckBox]);

  const isModuleAccessGranted = useCallback(() => {
    const modulePrivileges = access[accessItem as keyof typeof access];
    if (!modulePrivileges) return false;

    const currentModuleChecked = checkedArray.filter(privilege => 
      modulePrivileges.includes(privilege)
    );

    return modulePrivileges.length === currentModuleChecked.length;
  }, [checkedArray, accessItem]);

  const currentModulePrivileges = access[accessItem as keyof typeof access] || [];
  const dividedPrivileges = divideArray(currentModulePrivileges);

  return (
    <div className={lastIndex ? "" : "border-b"} key={accessItem}>
      <div 
        className={`flex justify-between h-[54px] items-center ${editLoading ? '' : 'cursor-pointer'}`} 
        onClick={() => !editLoading && setIsOpen(!isOpen)}
      >
        <div 
          className={`w-full h-[38px] flex items-center rounded-lg px-1.5 ${
            isOpen ? 'text-white bg-p_primary' : 'text-primary_text'
          }`} 
          data-testid={accessItem}
        >
          <p className="text-body1">{translate(accessItem)}</p>
        </div>
        <div className="w-9 flex justify-center cursor-pointer">
          <DropdownFramer isDropdownVisible={isModuleAccessGranted() ? false : isOpen}>
            <Image
              src={isModuleAccessGranted() ? 'right_mark' : isOpen ? "negative" : "plus"}
              className="cursor-pointer"
            />
          </DropdownFramer>
        </div>
      </div>
      {isOpen && (
        <DropdownItemsFramer>
          <div 
            className={`flex justify-between ${notEnglish ? 'pl-9 pr-1.5' : 'pl-1.5 pr-9'}`} 
            dir={notEnglish ? 'rtl' : 'ltr'}
          >
            <CheckboxMap
              options={dividedPrivileges[0]}
              handelCheckBox={handleScopedCheckbox}
              checkedArray={checkedArray}
              className="flex-col w-1/3"
            />
            <CheckboxMap
              options={dividedPrivileges[1]}
              className="flex-col w-1/3"
              handelCheckBox={handleScopedCheckbox}
              checkedArray={checkedArray}
            />
            <CheckboxMap
              options={dividedPrivileges[2]}
              className="flex-col w-1/3"
              handelCheckBox={handleScopedCheckbox}
              checkedArray={checkedArray}
            />
          </div>
        </DropdownItemsFramer>
      )}
    </div>
  );
};

export default MapPrivilege;